// Super-user-only admin page: enter / rotate / revoke per-Account Coinbase
// Exchange credentials. The plaintext key is never displayed once stored —
// only `****XXXX` last-four as a sanity check. POST to the backend encrypts
// the triplet at rest (Fernet), GET returns metadata only.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Kicker } from '../../components/shell';
import { api } from '../../lib/api';

interface CredentialMeta {
  account_id: string;
  has_credentials: boolean;
  key_last_four: string | null;
  updated_at: string | null;
}

interface TestResult {
  ok: boolean;
  row_count?: number;
  nonzero_count?: number;
  latency_ms?: number;
  error_kind?: string;
  status?: number;
  detail?: string;
}

export default function AccountCredentials() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();

  const [meta, setMeta] = useState<CredentialMeta | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [key, setKey] = useState('');
  const [secret, setSecret] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveBanner, setSaveBanner] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!accountId) return;
    try {
      setMeta(await api<CredentialMeta>(`/ops/accounts/${accountId}/credentials`));
      setLoadError(null);
    } catch (e: any) {
      setLoadError(e?.status === 403
        ? 'Super-user access required.'
        : 'Failed to load credentials.');
    }
  }, [accountId]);

  useEffect(() => { void reload(); }, [reload]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountId) return;
    setSaving(true); setSaveBanner(null); setSaveError(null);
    try {
      const resp = await api<CredentialMeta>(`/ops/accounts/${accountId}/credentials`, {
        method: 'POST',
        body: { key: key.trim(), secret: secret.trim(), passphrase: passphrase.trim() },
      });
      setKey(''); setSecret(''); setPassphrase('');
      setMeta(resp);
      const when = resp.updated_at
        ? new Date(resp.updated_at).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
          })
        : 'just now';
      setSaveBanner(`Saved · key ****${resp.key_last_four} · ${when}`);
    } catch (e: any) {
      const detail = e?.payload?.detail;
      setSaveError(typeof detail === 'string' ? detail : 'Failed to save credentials.');
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    if (!accountId) return;
    setTesting(true); setTestResult(null);
    try {
      const r = await api<TestResult>(
        `/ops/accounts/${accountId}/credentials/test`, { method: 'POST' },
      );
      if (r.ok) {
        setTestResult(
          `Connection OK · ${r.row_count} accounts visible · ${r.nonzero_count} with balance · ${r.latency_ms} ms`,
        );
      } else {
        const tag = r.error_kind === 'auth_failed' ? 'BAD KEY'
          : r.error_kind === 'missing_credentials' ? 'NO KEY'
          : r.error_kind === 'venue_error' ? 'VENUE DOWN'
          : 'FAILED';
        setTestResult(`${tag}: ${r.detail || r.error_kind || 'unknown error'}`);
      }
    } catch (e: any) {
      setTestResult(`Request failed: ${e?.payload?.detail || 'check console'}`);
    } finally {
      setTesting(false);
    }
  };

  const onRevoke = async () => {
    if (!accountId) return;
    if (!window.confirm('Revoke stored credentials for this account? The next snapshot will fall back to env vars (or skip if none).')) return;
    try {
      await api(`/ops/accounts/${accountId}/credentials`, { method: 'DELETE' });
      setMeta({ account_id: accountId, has_credentials: false, key_last_four: null, updated_at: null });
      setSaveBanner('Credentials revoked.');
    } catch {
      setSaveError('Failed to revoke.');
    }
  };

  if (loadError) {
    return (
      <div>
        <Kicker>Admin · Credentials</Kicker>
        <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--accent)' }}>{loadError}</p>
        <button className="btn-link" onClick={() => navigate('/clients')}>← Back to clients</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="section-head" style={{ marginBottom: 32 }}>
        <Kicker>Admin · Exchange credentials</Kicker>
        <h1 className="page-title">Per-account <em>API key.</em></h1>
        <p className="serif-body" style={{ marginTop: 12, color: 'var(--ink-2)' }}>
          Coinbase Exchange API keys are scoped to one profile each. Mint a fresh
          View-only key from inside this account's Coinbase profile, then paste
          the triplet here. The key is encrypted at rest (Fernet) and never
          displayed again — only the last four characters.
        </p>
      </div>

      {meta && (
        <div style={{
          marginBottom: 32, padding: '20px 24px',
          border: '1px solid var(--ink-3)',
          background: meta.has_credentials ? 'var(--paper-soft, #f8f6f0)' : 'transparent',
        }}>
          <div className="label" style={{ marginBottom: 8 }}>Current status</div>
          {meta.has_credentials ? (
            <div className="serif-body">
              Configured · key ends in <strong className="num">****{meta.key_last_four}</strong>
              {meta.updated_at && (
                <span style={{ marginLeft: 16, color: 'var(--ink-3)', fontSize: 13 }}>
                  updated {new Date(meta.updated_at).toLocaleString('en-US', {
                    year: 'numeric', month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </span>
              )}
              <button className="btn-link" style={{ marginLeft: 16 }} onClick={onRevoke}>
                Revoke →
              </button>
            </div>
          ) : (
            <div className="serif-body" style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}>
              No credentials stored. Snapshots for this account will fall back
              to the env-suffix key (if any), else SKIPPED.
            </div>
          )}
        </div>
      )}

      <form onSubmit={onSave}>
        <Field label="API key">
          <input
            type="password" autoComplete="off" required
            value={key} onChange={e => setKey(e.target.value)}
            placeholder="paste the new key"
          />
        </Field>
        <Field label="API secret (base64)">
          <input
            type="password" autoComplete="off" required
            value={secret} onChange={e => setSecret(e.target.value)}
            placeholder="paste the base64 secret"
          />
        </Field>
        <Field label="Passphrase">
          <input
            type="password" autoComplete="off" required
            value={passphrase} onChange={e => setPassphrase(e.target.value)}
            placeholder="paste the passphrase"
          />
        </Field>

        {saveError && (
          <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--accent)', margin: '12px 0' }}>
            {saveError}
          </p>
        )}
        {saveBanner && (
          <p style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)', margin: '12px 0' }}>
            {saveBanner}
          </p>
        )}

        <div style={{ marginTop: 24, display: 'flex', gap: 16 }}>
          <button type="submit" className="btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save credentials'} <span className="btn-arrow">→</span>
          </button>
          <button type="button" className="btn-link" onClick={() => navigate('/clients')}>
            ← Back to clients
          </button>
          <button
            type="button" className="btn-link" onClick={onTest}
            disabled={testing || !meta?.has_credentials}
            style={{ marginLeft: 'auto' }}
          >
            {testing ? 'Testing…' : 'Test connection →'}
          </button>
        </div>
        {testResult && (
          <p style={{
            fontFamily: 'var(--mono)', fontSize: 11, marginTop: 12,
            color: testResult.startsWith('Connection OK') ? 'var(--accent)' : 'var(--oxblood, #8a3030)',
          }}>
            {testResult}
          </p>
        )}
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{
        display: 'block', marginBottom: 6,
        fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: 'var(--ink-3)',
      }}>
        {label}
      </label>
      <div style={{
        // input ships unstyled; force a uniform look here so we don't depend
        // on a global form rule existing.
      }}>
        {children}
      </div>
    </div>
  );
}
